import * as i0 from '@angular/core';
import { InjectionToken, forwardRef, EventEmitter, Output, Input, Inject, Optional, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

const UI_SWITCH_OPTIONS = new InjectionToken('UI_SWITCH_OPTIONS');

const UI_SWITCH_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => E11SwitchComponent),
    multi: true,
};
class E11SwitchComponent {
    // NOTE: Intentionally not using getters and setters
    constructor(config = {}, cdr) {
        this.cdr = cdr;
        /**
         * The default unique identifier for the switch component.
         * @type {string}
         * @default random string
         * @example 'switch-156481'
         */
        this.id = `switch-${Math.ceil(Math.random() * 1000000).toString()}`; // default ID
        /**
         * The size of the switch component.
         * @type {Size | undefined}
         * @default 'md'
         */
        this.size = 'md';
        /**
         * The color of the switch component.
         * @type {string | undefined}
         * @default ''
         */
        this.color = '';
        /**
         * The label for the switch component.
         * @type {string | undefined}
         * @default ''
         */
        this.label = '';
        /**
         * The class overrides for the switch component.
         * @deprecated This property is deprecated. Please use {@link containerClass} instead.
         * @type {string | undefined}
         * @default ''
         */
        this.containerClassOverrides = '';
        /**
         * The class overrides for the switch component.
         * @type {string | undefined}
         * @default ''
         */
        this.containerClass = '';
        /**
         * The disabled state of the switch component.
         * @type {boolean | undefined}
         * @default false
         */
        this.disabled = false;
        /**
         * The checked state of the switch component.
         * @type {boolean | undefined}
         * @default false
         */
        this.checked = false;
        /**
         * The loading state of the switch component.
         * @type {boolean | undefined}
         * @default false
         */
        this.loading = false;
        /**
         * The spinner loading state of the switch component.
         * @type {boolean | undefined}
         * @default false
         */
        this.spinnerLoading = false;
        /**
         * Emits the checked state when the switch is toggled.
         * @deprecated This event emitter is deprecated. Please use {@link onChange} instead.
         * @type {EventEmitter<boolean>}
         */
        this.changes = new EventEmitter();
        /**
         * Emits the checked state when the switch is toggled.
         * @type {EventEmitter<boolean>}
         */
        this.onChange = new EventEmitter();
        /**
         * Emits the checked state when the switch is toggled.
         * @deprecated This event emitter is deprecated. Please use {@link onChangeEvent} instead.
         * @type {EventEmitter<MouseEvent>}
         */
        this.changeEvent = new EventEmitter();
        /**
         * Emits the checked state when the switch is toggled.
         * @type {EventEmitter<MouseEvent>}
         */
        this.OnchangeEvent = new EventEmitter();
        this.onTouchedCallback = (v) => { };
        this.onChangeCallback = (v) => { };
    }
    /**
     * Toggles the switch component.
     * @param {MouseEvent} event The mouse event.
     * @returns {void}
     */
    toggleSwitch(event) {
        this.checked = !this.checked;
        // TODO: remove this in the next major release
        this.changes.emit(this.checked);
        this.onChange.emit(this.checked);
        // TODO: remove this in the next major release
        this.changeEvent.emit(event);
        this.OnchangeEvent.emit(event);
    }
    /**
     * Writes the value to the switch component.
     * @param {any} obj The value to write.
     * @returns {void}
     * @implements {ControlValueAccessor}
     * @see {@link ControlValueAccessor}
     */
    writeValue(obj) {
        if (obj !== this.checked) {
            this.checked = !!obj;
        }
        /**
         * On change callback handler.
         * @param {any} v The value to write.
         * @returns {void}
         */
        this.onChangeCallback(this.checked);
        if (this.cdr) {
            this.cdr.markForCheck();
        }
    }
    /**
     * Registers the on change callback.
     * @param {any} fn The callback function.
     * @returns {void}
     * @implements {ControlValueAccessor}
     * @see {@link ControlValueAccessor}
     */
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    /**
     * Registers the on touched callback.
     * @param {any} fn The callback function.
     * @returns {void}
     * @implements {ControlValueAccessor}
     * @see {@link ControlValueAccessor}
     */
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    ngOnDestroy() {
        if (this._beforeChange) {
            this._beforeChange.unsubscribe();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SwitchComponent, deps: [{ token: UI_SWITCH_OPTIONS, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11SwitchComponent, isStandalone: true, selector: "e11-switch", inputs: { id: "id", size: "size", color: "color", label: "label", containerClassOverrides: "containerClassOverrides", containerClass: "containerClass", disabled: "disabled", disabledText: "disabledText", checked: "checked", loading: "loading", spinnerLoading: "spinnerLoading" }, outputs: { changes: "changes", onChange: "onChange", changeEvent: "changeEvent", OnchangeEvent: "OnchangeEvent" }, providers: [UI_SWITCH_CONTROL_VALUE_ACCESSOR], ngImport: i0, template: `
    <div
      class="e11-switch-container e11-relative {{ color }} {{ size }} {{ containerClassOverrides }} {{ containerClass }}"
      [attr.title]="disabled ? disabledText : null"
    >
      <div
        class="e11-switch e11-inline-block"
        [ngClass]="{ 'e11-switch-disabled': disabled, 'e11-switch-loading': spinnerLoading, 'e11-cursor-wait': loading }"
      >
        <input type="checkbox" id="{{ id }}" (click)="!disabled && toggleSwitch($event)" [checked]="checked" />
        <label for="{{ id }}" [ngClass]="{'e11-skeleton e11-hide-after e11-pointer-events-none': loading,}"></label>
        <span class="e11-switch-label"> {{ label }} </span>
      </div>
    </div>
  `, isInline: true, styles: [".e11-switch-container{display:inline-flex}.e11-switch-container .e11-switch.e11-switch-disabled label{cursor:not-allowed!important;opacity:.65}.e11-switch-container .e11-switch input[type=checkbox]{height:0;width:0;visibility:hidden}.e11-switch-container .e11-switch input[type=checkbox]:checked+label:after{left:calc(100% - 1px);transform:translate(-100%)}.e11-switch-container .e11-switch.e11-switch.e11-switch-loading input[type=checkbox]:checked+label:after{left:0}.e11-switch-container .e11-switch label{cursor:pointer;width:42px;height:66px;display:block;border-radius:21px;position:absolute;top:0;left:0}.e11-switch-container .e11-switch label:after{content:\"\";position:absolute;top:1px;left:1px;width:40px;height:40px;border-radius:50%}.e11-switch-container.xs .e11-switch label{height:22px;width:36px;border-radius:16px}.e11-switch-container.xs .e11-switch label:after{height:20px;width:20px}.e11-switch-container.xs .e11-switch-label{margin-left:2.5rem;line-height:1.4rem;font-size:14px}.e11-switch-container.sm .e11-switch label{height:32px;width:52px;border-radius:16px}.e11-switch-container.sm .e11-switch label:after{height:30px;width:30px}.e11-switch-container.sm .e11-switch-label{margin-left:3.5rem;line-height:32px}.e11-switch-container.md .e11-switch label{height:42px;width:66px;border-radius:21px;transition:all .3s ease-in-out}.e11-switch-container.md .e11-switch.e11-switch-loading label{width:42px;transition:all .3s ease-in-out}.e11-switch-container.md .e11-switch label:after{height:40px;width:40px}.e11-switch-container.md .e11-switch.e11-switch-loading label:after{border:3px solid;border-top:3px solid;border-color:rgba(var(--color-grey),var(--tw-border-opacity, 1));--tw-border-opacity: 1;border-top-color:rgba(var(--color-primary),var(--tw-border-opacity, 1));transition:.3s ease;transition-property:background-color;animation:spin 1s ease infinite}.e11-switch-container.md .e11-switch-label{margin-left:4.5rem;line-height:42px}.e11-switch-container.lg .e11-switch label{height:52px;width:82px;border-radius:26px}.e11-switch-container.lg .e11-switch label:after{height:50px;width:50px}.e11-switch-container.lg .e11-switch-label{margin-left:5.5rem;line-height:52px}.e11-switch-container.primary .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-primary),var(--tw-bg-opacity, 1))}.e11-switch-container.primary-accent .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-primary-accent),var(--tw-bg-opacity, 1))}.e11-switch-container.secondary .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-secondary),var(--tw-bg-opacity, 1))}.e11-switch-container.neutral .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-grey),var(--tw-bg-opacity, 1))}.e11-switch-container.success .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-success),var(--tw-bg-opacity, 1))}.e11-switch-container .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-primary),var(--tw-bg-opacity, 1))}.e11-switch-container .e11-switch label{--tw-bg-opacity: 1;background-color:rgba(var(--color-grey),var(--tw-bg-opacity, 1))}.e11-switch-container .e11-switch label:after{--tw-bg-opacity: 1;background-color:rgb(255 255 255 / var(--tw-bg-opacity, 1));--tw-shadow: 0 1px 2px 0 rgb(0 0 0 / .05);--tw-shadow-colored: 0 1px 2px 0 var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.e11-hide-after:after{content:none!important}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SwitchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-switch', standalone: true, imports: [CommonModule, FormsModule], providers: [UI_SWITCH_CONTROL_VALUE_ACCESSOR], encapsulation: ViewEncapsulation.None, template: `
    <div
      class="e11-switch-container e11-relative {{ color }} {{ size }} {{ containerClassOverrides }} {{ containerClass }}"
      [attr.title]="disabled ? disabledText : null"
    >
      <div
        class="e11-switch e11-inline-block"
        [ngClass]="{ 'e11-switch-disabled': disabled, 'e11-switch-loading': spinnerLoading, 'e11-cursor-wait': loading }"
      >
        <input type="checkbox" id="{{ id }}" (click)="!disabled && toggleSwitch($event)" [checked]="checked" />
        <label for="{{ id }}" [ngClass]="{'e11-skeleton e11-hide-after e11-pointer-events-none': loading,}"></label>
        <span class="e11-switch-label"> {{ label }} </span>
      </div>
    </div>
  `, styles: [".e11-switch-container{display:inline-flex}.e11-switch-container .e11-switch.e11-switch-disabled label{cursor:not-allowed!important;opacity:.65}.e11-switch-container .e11-switch input[type=checkbox]{height:0;width:0;visibility:hidden}.e11-switch-container .e11-switch input[type=checkbox]:checked+label:after{left:calc(100% - 1px);transform:translate(-100%)}.e11-switch-container .e11-switch.e11-switch.e11-switch-loading input[type=checkbox]:checked+label:after{left:0}.e11-switch-container .e11-switch label{cursor:pointer;width:42px;height:66px;display:block;border-radius:21px;position:absolute;top:0;left:0}.e11-switch-container .e11-switch label:after{content:\"\";position:absolute;top:1px;left:1px;width:40px;height:40px;border-radius:50%}.e11-switch-container.xs .e11-switch label{height:22px;width:36px;border-radius:16px}.e11-switch-container.xs .e11-switch label:after{height:20px;width:20px}.e11-switch-container.xs .e11-switch-label{margin-left:2.5rem;line-height:1.4rem;font-size:14px}.e11-switch-container.sm .e11-switch label{height:32px;width:52px;border-radius:16px}.e11-switch-container.sm .e11-switch label:after{height:30px;width:30px}.e11-switch-container.sm .e11-switch-label{margin-left:3.5rem;line-height:32px}.e11-switch-container.md .e11-switch label{height:42px;width:66px;border-radius:21px;transition:all .3s ease-in-out}.e11-switch-container.md .e11-switch.e11-switch-loading label{width:42px;transition:all .3s ease-in-out}.e11-switch-container.md .e11-switch label:after{height:40px;width:40px}.e11-switch-container.md .e11-switch.e11-switch-loading label:after{border:3px solid;border-top:3px solid;border-color:rgba(var(--color-grey),var(--tw-border-opacity, 1));--tw-border-opacity: 1;border-top-color:rgba(var(--color-primary),var(--tw-border-opacity, 1));transition:.3s ease;transition-property:background-color;animation:spin 1s ease infinite}.e11-switch-container.md .e11-switch-label{margin-left:4.5rem;line-height:42px}.e11-switch-container.lg .e11-switch label{height:52px;width:82px;border-radius:26px}.e11-switch-container.lg .e11-switch label:after{height:50px;width:50px}.e11-switch-container.lg .e11-switch-label{margin-left:5.5rem;line-height:52px}.e11-switch-container.primary .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-primary),var(--tw-bg-opacity, 1))}.e11-switch-container.primary-accent .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-primary-accent),var(--tw-bg-opacity, 1))}.e11-switch-container.secondary .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-secondary),var(--tw-bg-opacity, 1))}.e11-switch-container.neutral .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-grey),var(--tw-bg-opacity, 1))}.e11-switch-container.success .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-success),var(--tw-bg-opacity, 1))}.e11-switch-container .e11-switch input[type=checkbox]:checked+label{--tw-bg-opacity: 1;background-color:rgba(var(--color-primary),var(--tw-bg-opacity, 1))}.e11-switch-container .e11-switch label{--tw-bg-opacity: 1;background-color:rgba(var(--color-grey),var(--tw-bg-opacity, 1))}.e11-switch-container .e11-switch label:after{--tw-bg-opacity: 1;background-color:rgb(255 255 255 / var(--tw-bg-opacity, 1));--tw-shadow: 0 1px 2px 0 rgb(0 0 0 / .05);--tw-shadow-colored: 0 1px 2px 0 var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.e11-hide-after:after{content:none!important}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [UI_SWITCH_OPTIONS]
                }, {
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { id: [{
                type: Input
            }], size: [{
                type: Input
            }], color: [{
                type: Input
            }], label: [{
                type: Input
            }], containerClassOverrides: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], disabled: [{
                type: Input
            }], disabledText: [{
                type: Input
            }], checked: [{
                type: Input
            }], loading: [{
                type: Input
            }], spinnerLoading: [{
                type: Input
            }], changes: [{
                type: Output
            }], onChange: [{
                type: Output
            }], changeEvent: [{
                type: Output
            }], OnchangeEvent: [{
                type: Output
            }] } });

class E11SwitchModule {
    static forRoot(config) {
        return {
            ngModule: E11SwitchModule,
            providers: [{ provide: UI_SWITCH_OPTIONS, useValue: config || {} }],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SwitchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11SwitchModule, imports: [E11SwitchComponent], exports: [FormsModule, E11SwitchComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SwitchModule, imports: [E11SwitchComponent, FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SwitchModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11SwitchComponent],
                    exports: [FormsModule, E11SwitchComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11SwitchComponent, E11SwitchModule };
